import { CardOnFileRow } from "@/features/patient/components/patient-details-drawer";
import { useInitializeStripe } from "@/features/stripe/hooks/useInitializeStripe";
import { StaffCardCapturePanel } from "./StaffCardCapturePanel";
import { Spinner } from "@/components/ui/Spinner";
import { usePatientDetailsContext } from "@/features/patient/context/PatientDetailsContext";
import { useCaptureSession } from "../hooks/useCaptureSession";
import { useCallback, useEffect, useRef } from "react";
import { useCardCaptureSuccessHandler } from "../hooks/useCardCaptureSuccessHandler";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { getFullName } from "@/lib/string";

const StaffCardCaptureContainer = () => {
  const { tenantId } = useTenantRouteContext();
  const { mutateAsync } = useCaptureSession();
  const { patient, patientId } = usePatientDetailsContext();

  const loadSession = useCallback(async () => {
    return await mutateAsync(patientId!);
  }, [mutateAsync, patientId]);

  const patientLoaded = !!patient;
  const hasCard = patientLoaded && !!patient.cardOnFile;

  const { state, handleInitialize, markSuccess, resetState } =
    useInitializeStripe({
      loadSession,
      enabled: !hasCard,
    });
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  });

  useEffect(() => {
    const { phase } = stateRef.current;

    if (!patientLoaded || phase === "none") return;
    if (hasCard && phase === "success") return;
    if (
      !hasCard &&
      (phase === "error" || phase === "initializing" || phase === "ready")
    )
      return;

    // if we got here, our local phase no longer matches hasCard, e.g. we're
    // in "success" but the card was deleted right after - likely a SignalR
    // event our own state (or Stripe's) doesn't know about, so reset
    resetState();
  }, [patientLoaded, hasCard, resetState]);

  const handleSuccess = useCardCaptureSuccessHandler(
    patientId ?? "",
    tenantId,
    markSuccess,
  );

  const render = () => {
    if (state.phase === "initializing") {
      return <Spinner />;
    }

    if (state.phase === "none" && patient?.cardOnFile) {
      return (
        <CardOnFileRow
          card={patient.cardOnFile}
          patientFullName={getFullName(patient)}
          patientId={patientId ?? ""}
          practiceId={patient.practiceId}
        />
      );
    }

    return (
      <StaffCardCapturePanel
        stripeState={state}
        onSuccess={handleSuccess}
        onRetry={handleInitialize}
        patientId={patientId ?? ""}
      />
    );
  };

  return <div className="px-5 pt-5 pb-6">{render()}</div>;
};

export default StaffCardCaptureContainer;
